import React, { useEffect, useState } from 'react'
import { Button, Pagination, Modal, Form, Input } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState, useAppDispatch } from '~/Store/hooks'
import { UserActions } from '~/Store/User'
import { UserService } from '~/Services'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import utc from 'dayjs/plugin/utc'
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons'
import './AdminClaims.css'
import { ClaimActions } from '~/Store/Claims'
import { ClaimService } from '~/Services/Claim'

dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.locale('en')
dayjs.locale('vi')
dayjs.extend(utc)

export const AdminClaims = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const claimData = useSelector((state: RootState) => state.claim.listClaimData)
  const [currentRecord, setCurrentRecord] = useState<any>(null)
  const [isModalDetailVisible, setIsModalDetailVisible] = useState(false)
  const [isModalAddVisible, setIsModalAddVisible] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 15
  const [form] = Form.useForm()
  const [productClaimId, setProductClaimId] = useState<string | null>(null)

  const [claimTypeAdd, setClaimTypeAdd] = useState('')
  const [claimValueAdd, setClaimValueAdd] = useState('')
  const [productIdAdd, setProductIdAdd] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<number[]>([])

  const toggleGroupVisibility = (productId: number) => {
    setExpandedGroups(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId],
    )
  }

  const fetchListClaim = async () => {
    try {
      dispatch(ClaimActions.getClaimsRequest())
      const req: any = await ClaimService.Get.getClaims({
        params: {
          pageSize: 1000,
        },
      })

      const { data } = req
      dispatch(ClaimActions.setClaimsSuccess(data))
    } catch (err) {
      console.error('Error fetching claims list:', err)
      dispatch(UserActions.setListOrganizationDataFailure(err))
    }
  }

  useEffect(() => {
    fetchListClaim()
  }, [dispatch])

  const handleUpdateProductClaim = async () => {
    try {
      const claimType = form.getFieldValue('claimType')
      const claimValue = form.getFieldValue('claimValue')
      const productId = form.getFieldValue('productId')

      const input = {
        claimType: claimType,
        claimValue: claimValue,
        productId: productId,
      }

      const req: any = await ClaimService.Put.updateProductClaim(
        productClaimId,
        input,
      )

      fetchListClaim()
      setIsModalDetailVisible(false)
    } catch (err) {
      console.error('Error updating license expiration date:', err)
    }
    setIsModalDetailVisible(false)
  }

  const handleDelete = async (record: any) => {
    console.log('Delete record:', record)
    try {
      dispatch(UserActions.deleteClaimRequest())
      const req: any = await UserService.Delete.deleteClaim(record.productId)

      if (req === 1) {
        console.log('User deleted successfully')
        const updatedLicenses = currentRecord?.licenses?.filter(
          (license: any) =>
            license.users.every((u: any) => u.id !== record.productId),
        )
        setCurrentRecord({
          ...currentRecord,
          licenses: updatedLicenses,
        })
      } else if (req === -1) {
        console.error('Error deleting user: Operation failed')
      } else {
        console.error('Unexpected API response:', req)
      }
    } catch (err) {
      console.error('Error deleting user:', err)
    }
  }

  const handleAddClaim = async () => {
    try {
      const input = {
        claimType: claimTypeAdd,
        claimValue: claimValueAdd,
        productId: productIdAdd,
      }
      const req: any = await ClaimService.Post.addNewProductClaim(input)

      fetchListClaim()
      setIsModalDetailVisible(false)
    } catch (err) {
      console.error('Error updating license expiration date:', err)
    }
    setIsModalDetailVisible(false)
  }

  const dataSource =
    claimData?.results.map((org: any) => ({
      productClaimId: org.guid,
      claimType: org.claimType,
      claimValue: org.claimValue,
      productId: org.productId,
    })) || []

  const groupedData = dataSource.reduce((acc: any, item: any) => {
    if (!acc[item.productId]) {
      acc[item.productId] = []
    }
    acc[item.productId].push(item)
    return acc
  }, {})

  const groupedDataArray = Object.keys(groupedData).map(key => ({
    productId: key,
    items: groupedData[key],
  }))

  const paginatedData = groupedDataArray.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  )

  const handleEdit = (record: any) => {
    console.log('Edit record:', record)
    setProductClaimId(record.productClaimId)
    setCurrentRecord(record)
    form.setFieldsValue({
      claimType: record.claimType,
      claimValue: record.claimValue,
      productId: record.productId,
    })
    setIsModalDetailVisible(true)
  }

  const handleDetailModalClose = () => {
    setIsModalDetailVisible(false)
    setCurrentRecord(null)
  }

  const handleAdd = (productId: string) => {
    form.resetFields()
    setIsModalAddVisible(true)
    setCurrentRecord(null)
    setProductIdAdd(productId)
    setClaimTypeAdd('')
    setClaimValueAdd('')
  }

  const handleAddModalClose = () => {
    setIsModalAddVisible(false)
    form.resetFields()
  }

  return (
    <div className="custom-table">
      <div className="custom-table-container">
        {paginatedData.map((group: any) => (
          <div key={group.productId} className="custom-table-group">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onClick={() => {
                toggleGroupVisibility(group.productId)
                console.log(group)
              }}
            >
              <h4>{t('admin.productId', { id: group.productId })}</h4>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <PlusCircleOutlined
                  className="custom-plus-icon"
                  onClick={e => {
                    e.stopPropagation()
                    handleAdd(group.productId)
                  }}
                />
                {expandedGroups.includes(group.productId) ? (
                  <CaretUpOutlined className="custom-updown-icon" />
                ) : (
                  <CaretDownOutlined className="custom-updown-icon" />
                )}
              </div>
            </div>

            {expandedGroups.includes(group.productId) && (
              <div className="custom-items-list">
                {group.items.map((item: any) => (
                  <div key={item.productClaimId} className="custom-table-row">
                    <div className="custom-table-cell">
                      {item.claimType || ''}
                    </div>
                    <div className="custom-table-cell">
                      {item.claimValue || ''}
                    </div>
                    <div className="custom-table-cell">
                      {item.productClaimId || ''}
                    </div>
                    <div className="custom-table-cell actions">
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={e => {
                          e.stopPropagation()
                          handleEdit(item)
                        }}
                      />
                      <Button
                        type="link"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={e => {
                          e.stopPropagation()
                          // handleDelete(item)
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <Pagination
        className="custom-pagination"
        current={currentPage}
        pageSize={pageSize}
        total={groupedDataArray.length}
        onChange={page => setCurrentPage(page)}
      />
      <Modal
        title={t('admin.claimDetailInfo')}
        visible={isModalDetailVisible}
        onCancel={handleDetailModalClose}
        className="custom-modal-claim"
        footer={[
          <Button
            key="cancel"
            className="custom-btn"
            onClick={handleDetailModalClose}
          >
            {t('cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            className="custom-btn"
            onClick={handleUpdateProductClaim}
          >
            {t('save')}
          </Button>,
        ]}
      >
        {currentRecord ? (
          <Form form={form} layout="vertical">
            <Form.Item
              label={t('admin.claimType')}
              name="claimType"
              initialValue={currentRecord.claimType}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('admin.claimValue')}
              name="claimValue"
              initialValue={currentRecord.claimValue}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('admin.productClaimId')}
              name="productId"
              initialValue={currentRecord.productId}
            >
              <Input />
            </Form.Item>
          </Form>
        ) : (
          <p></p>
        )}
      </Modal>
      <Modal
        title={t('admin.addNewProductClaim')}
        visible={isModalAddVisible}
        onCancel={handleAddModalClose}
        className="custom-modal"
        footer={[
          <Button
            key="cancel"
            className="custom-btn"
            onClick={handleAddModalClose}
          >
            {t('cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            className="custom-btn"
            onClick={handleAddClaim}
          >
            {t('save')}
          </Button>,
        ]}
      >
        <div>
          <label htmlFor="claimType">{t('admin.claimType')}</label>
          <Input
            id="claimType"
            value={claimTypeAdd}
            onChange={e => setClaimTypeAdd(e.target.value)}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <label htmlFor="claimValue">{t('admin.claimValue')}</label>
          <Input
            id="claimValue"
            value={claimValueAdd}
            onChange={e => setClaimValueAdd(e.target.value)}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <label htmlFor="productId">{t('admin.productClaimId')}</label>
          <Input
            id="productId"
            value={productIdAdd}
            onChange={e => setProductIdAdd(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  )
}

export default AdminClaims